/**
 * MetaMask / Polygon helpers — send USDC from the user's wallet directly.
 *
 * No SDK needed: we build the ERC-20 transfer calldata manually and use
 * `window.ethereum.request` for the tx. This keeps the bundle small and works
 * with MetaMask, Coinbase Wallet, Rabby, Brave Wallet, Trust, etc.
 */

export const POLYGON_CHAIN_ID = '0x89'; // 137
export const POLYGON_PARAMS = {
  chainId: POLYGON_CHAIN_ID,
  chainName: 'Polygon Mainnet',
  nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 },
  rpcUrls: ['https://polygon-rpc.com'],
  blockExplorerUrls: ['https://polygonscan.com'],
};

export const USDC_POLYGON_ADDRESS = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174';
const USDC_DECIMALS = 6;

export function hasEthereumProvider() {
  return typeof window !== 'undefined' && !!window.ethereum;
}

/** Safely pads a hex string to 64 chars (32 bytes). */
function padHex(hex) {
  return hex.replace(/^0x/, '').padStart(64, '0');
}

/** Build ERC-20 `transfer(address,uint256)` calldata. */
export function buildUsdcTransferData(toAddress, amountUsdc) {
  const selector = 'a9059cbb'; // keccak256("transfer(address,uint256)")[:4]
  const to = padHex(toAddress.toLowerCase());
  // Convert amount to 6-decimals base units. Use BigInt for precision.
  const scaled = BigInt(Math.round(amountUsdc * 10 ** USDC_DECIMALS));
  const amount = padHex(scaled.toString(16));
  return '0x' + selector + to + amount;
}

/** Request wallet connect. Returns the first account. */
export async function connectWallet() {
  if (!hasEthereumProvider()) {
    throw new Error('No wallet detected. Install MetaMask to continue.');
  }
  const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
  if (!accounts || !accounts[0]) throw new Error('No account returned from wallet');
  return accounts[0];
}

/** Switch to Polygon (and add it if the wallet doesn't have it). */
export async function ensurePolygon() {
  if (!hasEthereumProvider()) throw new Error('No wallet detected');
  try {
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: POLYGON_CHAIN_ID }],
    });
  } catch (err) {
    // 4902 = chain not added
    if (err?.code === 4902 || /Unrecognized chain/i.test(err?.message || '')) {
      await window.ethereum.request({
        method: 'wallet_addEthereumChain',
        params: [POLYGON_PARAMS],
      });
    } else {
      throw err;
    }
  }
}

/**
 * Send a USDC transfer on Polygon from the connected wallet.
 * Returns the tx hash once the user confirms.
 */
export async function sendUsdcPayment({ from, to, amountUsdc }) {
  if (!hasEthereumProvider()) throw new Error('No wallet detected');
  const data = buildUsdcTransferData(to, amountUsdc);
  const tx = {
    from,
    to: USDC_POLYGON_ADDRESS,
    value: '0x0',
    data,
  };
  const txHash = await window.ethereum.request({
    method: 'eth_sendTransaction',
    params: [tx],
  });
  return txHash;
}
